import { useEffect, useState } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import Card from '@mui/material/Card';
import TableRow from '@mui/material/TableRow';
import {
  Box,
  Button,
  Container,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  CircularProgress,
} from '@mui/material';
import Page from '../../components/Page';
import { useAppSelector, useAppDispatch } from '../../store';
import {
  setLimit,
  setSkip,
  setCurrentPage,
  reset as resetAgents,
} from '../../store/agentManagement/agentManagementSlice';
import {
  findAgents,
  createAgent,
  updateAgent,
  deleteAgent,
} from '../../store/agentManagement/api';
import Loader from '../../components/Loader';
import Label from '../../components/Label';
import Iconify from '../../components/Iconify';
import { useFormik } from 'formik';
import useToggle from '../../hooks/useToggle';
import Scrollbar from '../../components/Scrollbar';
import Searchbar from '../../components/Searchbar';
import { omit } from 'lodash';
import agentSchema from '../../validation/agentSchema';
import AgentFormModal from './AgentFormModal';
import { Agent } from '../../store/agentManagement/api/interfaces';
import { fDateLine, fTimeLine } from '../../utils/formatTime';
import { agentDeactivationTransfer } from '../../store/agentDeactivationTransfer/api';
import { toast } from 'react-toastify';

const Index: React.FC = () => {
  const dispatch = useAppDispatch();
  const { data, totalRecords, limit, currentPage, skip, isLoading } =
    useAppSelector((state) => state.agentManagement);
  const [search, setSearch] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<'active' | 'inactive' | ''>('');
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
  const { toggle, onOpen, onClose } = useToggle();
  const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);
  const [agentToDeactivate, setAgentToDeactivate] = useState<Agent | null>(null);
  const [deactivationReason, setDeactivationReason] = useState('');
  const [isDeactivating, setIsDeactivating] = useState(false);

  useEffect(() => {
    dispatch(
      findAgents({
        limit,
        skip,
        ...(search && { search: search }),
        ...(statusFilter && { status: statusFilter }),
      })
    );
  }, [dispatch, limit, skip, search, statusFilter]);

  useEffect(() => {
    dispatch(resetAgents());
  }, [search, statusFilter]);

  const {
    handleSubmit,
    values,
    setFieldValue,
    resetForm,
    isSubmitting,
    errors,
    touched,
    handleChange,
    setValues,
    getFieldProps,
  } = useFormik({
    initialValues: {
      id: '',
      name: '',
      email: '',
      mobileNumber: '',
      whatsappNumber: '',
      password: '',
      countryCallingCode: '+974',
      countryCode: 'QA',
      language: 'en',
      activeState: true,
      photoId: '',
      brokerLicenseId: '',
      idAttachmentId: '',
    },
    validationSchema: agentSchema,
    onSubmit: async (values) => {
      try {
        if (values.id) {
          // For update: remove id, password (if empty), attachments, and userProfileImage
          const reqBody: any = {
            id: values.id,
            ...omit(values, ['id', 'password', 'attachments', 'userProfileImage']),
          };
          // Only include password if it's provided (not empty)
          if (values.password && values.password.trim()) {
            reqBody.password = values.password;
          }
          // Convert empty whatsappNumber to null
          if (!reqBody.whatsappNumber || reqBody.whatsappNumber.trim() === '') {
            reqBody.whatsappNumber = null;
          }
          await dispatch(updateAgent(reqBody)).unwrap();
        } else {
          // For create: remove id, attachments, and userProfileImage
          const reqBody = omit(values, ['id', 'attachments', 'userProfileImage']) as any;
          // Convert empty whatsappNumber to null
          if (!reqBody.whatsappNumber || reqBody.whatsappNumber.trim() === '') {
            reqBody.whatsappNumber = null;
          }
          await dispatch(createAgent(reqBody)).unwrap();
        }
        onClose();
        resetForm();
        setSelectedAgent(null);
        dispatch(
          findAgents({
            limit,
            skip,
            ...(search && { search: search }),
            ...(statusFilter && { status: statusFilter }),
          })
        );
      } catch (error: any) {
        console.error('Error submitting form:', error);
      }
    },
  });

  const handleUpdate = (value: Agent): void => {
    setSelectedAgent(value);
    onOpen();
    setValues({
      id: value?.id,
      name: value?.name || '',
      email: value?.email || '',
      mobileNumber: value?.mobileNumber || '',
      whatsappNumber: value?.whatsappNumber || '',
      password: '',
      countryCallingCode: value?.countryCallingCode || '+974',
      countryCode: value?.countryCode || 'QA',
      language: value?.language || 'en',
      activeState: value?.activeState ?? true,
      photoId: value?.userProfileImage?.id || '',
      brokerLicenseId: value?.attachments?.find((att: any) => att.documentType === 'broker_license')?.id || '',
      idAttachmentId: value?.attachments?.find((att: any) => att.documentType === 'id_attachment')?.id || '',
    });
  };

  const handleDelete = async (id: string) => {
    try {
      await dispatch(deleteAgent(id)).unwrap();
      dispatch(
        findAgents({
          limit,
          skip,
          ...(search && { search: search }),
          ...(statusFilter && { status: statusFilter }),
        })
      );
    } catch (error: any) {}
  };

  const handlePageChange = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number
  ): void => {
    dispatch(setSkip({ skip: limit * newPage }));
    dispatch(setCurrentPage({ currentPage: newPage }));
  };

  const handleDeactivateClick = (agent: Agent) => {
    setAgentToDeactivate(agent);
    setDeactivationReason('');
    setDeactivateDialogOpen(true);
  };

  const handleDeactivateConfirm = async () => {
    if (!agentToDeactivate) return;

    setIsDeactivating(true);
    try {
      const result = await dispatch(
        agentDeactivationTransfer({
          agentId: agentToDeactivate.id,
          reason: deactivationReason || undefined,
        })
      ).unwrap();

      toast.success(
        result.message ||
          `Agent deactivated successfully. ${result.adsTransferred || 0} ads and ${result.creditsTransferred || 0} credits transferred.`
      );

      setDeactivateDialogOpen(false);
      setAgentToDeactivate(null);
      setDeactivationReason('');

      // Refresh the list
      dispatch(
        findAgents({
          limit,
          skip,
          ...(search && { search: search }),
          ...(statusFilter && { status: statusFilter }),
        })
      );
    } catch (error: any) {
      console.error('Error deactivating agent:', error);
      toast.error(error?.message || 'Failed to deactivate agent.');
    } finally {
      setIsDeactivating(false);
    }
  };

  const handleDeactivateCancel = () => {
    setDeactivateDialogOpen(false);
    setAgentToDeactivate(null);
    setDeactivationReason('');
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    dispatch(setLimit({ limit: +event.target.value }));
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const value = e.target.value;
    setSearch(value);
    dispatch(setSkip({ skip: 0 }));
    dispatch(setCurrentPage({ currentPage: 0 }));
  };

  const handleStatusFilterChange = (value: 'active' | 'inactive' | '') => {
    setStatusFilter(value);
    dispatch(setSkip({ skip: 0 }));
    dispatch(setCurrentPage({ currentPage: 0 }));
  };

  return (
    <>
      <Page title={'Agent Management: List'}>
        <Container maxWidth={'xl'}>
          <Stack
            direction='row'
            alignItems='center'
            justifyContent='space-between'
            spacing={{ xs: 0.5, sm: 1.5 }}
            my={1}
          >
            <Select
              value={statusFilter}
              onChange={(e) => handleStatusFilterChange(e.target.value as 'active' | 'inactive' | '')}
              displayEmpty
              sx={{ minWidth: 150 }}
            >
              <MenuItem value=''>All Status</MenuItem>
              <MenuItem value='active'>Active</MenuItem>
              <MenuItem value='inactive'>Inactive</MenuItem>
            </Select>
            <Button
              variant='contained'
              startIcon={<Iconify icon={'eva:plus-fill'} />}
              onClick={onOpen}
            >
              {'Add Agent'}
            </Button>
          </Stack>

          <Card>
            <Searchbar handleSearch={handleSearch} />

            <Scrollbar>
              <TableContainer sx={{ minWidth: 1200, position: 'relative' }}>
                <Table
                  size={'medium'}
                  sx={{
                    borderCollapse: 'separate',
                    borderSpacing: '0 10px',
                  }}
                >
                  <TableHead
                    sx={{
                      background: '#f2d2df',
                    }}
                  >
                    <TableRow>
                      <TableCell align='center'>
                        <div style={{ color: '#64013c' }}>{'Name'}</div>
                      </TableCell>
                      <TableCell align='center'>
                        <div style={{ color: '#64013c' }}>{'Email'}</div>
                      </TableCell>
                      <TableCell align='center'>
                        <div style={{ color: '#64013c' }}>{'Mobile Number'}</div>
                      </TableCell>
                      <TableCell align='center'>
                        <div style={{ color: '#64013c' }}>{'WhatsApp Number'}</div>
                      </TableCell>
                      <TableCell align='center'>
                        <div style={{ color: '#64013c' }}>{'Status'}</div>
                      </TableCell>
                      <TableCell align='center'>
                        <div style={{ color: '#64013c' }}>
                          <div style={{ whiteSpace: 'nowrap' }}>{'Created Date'}</div>
                          <div style={{ whiteSpace: 'nowrap' }}>{'Time'}</div>
                        </div>
                      </TableCell>
                      <TableCell align='center'>{'Edit'}</TableCell>
                      <TableCell align='center'>{'Action'}</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {isLoading ? (
                      <TableRow hover>
                        <TableCell
                          colSpan={8}
                          align='center'
                        >
                          <Loader />
                        </TableCell>
                      </TableRow>
                    ) : data?.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={8}
                          align='center'
                        >
                          {'No record found'}
                        </TableCell>
                      </TableRow>
                    ) : (
                      data?.map((agent) => (
                        <TableRow key={agent.id}>
                          <TableCell align='center'>{agent?.name}</TableCell>
                          <TableCell align='center'>{agent?.email}</TableCell>
                          <TableCell align='center'>
                            {agent?.countryCallingCode}-{agent?.mobileNumber}
                          </TableCell>
                          <TableCell align='center'>
                            {agent?.whatsappNumber || '-'}
                          </TableCell>
                          <TableCell align='center'>
                            <Label
                              variant='ghost'
                              color={agent.activeState ? 'success' : 'error'}
                            >
                              {agent.activeState ? 'Active' : 'Inactive'}
                            </Label>
                          </TableCell>
                          <TableCell align='center'>
                            {agent.createdAt ? (
                              <Box sx={{ whiteSpace: 'nowrap' }}>
                                <Box component="span" sx={{ display: 'block' }}>
                                  {fDateLine(agent.createdAt)}
                                </Box>
                                <Box component="span" sx={{ display: 'block' }}>
                                  {fTimeLine(agent.createdAt)}
                                </Box>
                              </Box>
                            ) : '-'}
                          </TableCell>
                          <TableCell align='center'>
                            <IconButton
                              aria-label='edit'
                              onClick={() => handleUpdate(agent)}
                            >
                              <Iconify icon={'eva:edit-fill'} />
                            </IconButton>
                          </TableCell>
                          <TableCell align='center'>
                            <Stack direction="row" spacing={1} justifyContent="center">
                              {agent.activeState && (
                                <Tooltip title='Deactivate & Transfer'>
                                  <IconButton
                                    aria-label='deactivate'
                                    onClick={() => handleDeactivateClick(agent)}
                                    color='warning'
                                  >
                                    <Iconify icon={'eva:close-circle-outline'} />
                                  </IconButton>
                                </Tooltip>
                              )}
                              <Tooltip title='Delete'>
                                <IconButton
                                  aria-label='delete'
                                  onClick={() => handleDelete(agent.id)}
                                  color='error'
                                >
                                  <Iconify icon={'eva:trash-2-outline'} />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Scrollbar>

            <Box sx={{ position: 'relative' }}>
              <TablePagination
                rowsPerPageOptions={[10, 25, 50, 100]}
                component='div'
                count={totalRecords}
                rowsPerPage={limit}
                page={currentPage}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
              />
            </Box>
          </Card>
          <AgentFormModal
            touched={touched}
            errors={errors}
            handleChange={handleChange}
            handleSubmit={handleSubmit}
            isSubmitting={isSubmitting}
            onClose={() => {
              setSelectedAgent(null);
              onClose();
            }}
            resetForm={resetForm}
            setFieldValue={setFieldValue}
            toggle={toggle}
            values={{
              ...values,
              // Include attachments and userProfileImage from the selected agent for editing
              attachments: selectedAgent?.attachments || [],
              userProfileImage: selectedAgent?.userProfileImage,
            }}
            getFieldProps={getFieldProps}
          />

          {/* Deactivate Agent Dialog */}
          <Dialog
            open={deactivateDialogOpen}
            onClose={handleDeactivateCancel}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Deactivate Agent & Transfer Assets</DialogTitle>
            <DialogContent>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Are you sure you want to deactivate <strong>{agentToDeactivate?.name}</strong>?
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                This action will:
              </Typography>
              <Typography variant="body2" component="ul" sx={{ pl: 3, mb: 2 }}>
                <li>Transfer all agent's ads to your company account</li>
                <li>Transfer remaining agent credits to your company wallet</li>
                <li>Deactivate the agent account</li>
              </Typography>
              <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                This action cannot be undone.
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Reason (Optional)"
                placeholder="Enter reason for deactivation..."
                value={deactivationReason}
                onChange={(e) => setDeactivationReason(e.target.value)}
                sx={{ mt: 2 }}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={handleDeactivateCancel} disabled={isDeactivating}>
                Cancel
              </Button>
              <Button
                onClick={handleDeactivateConfirm}
                variant="contained"
                color="warning"
                disabled={isDeactivating}
                startIcon={isDeactivating ? <CircularProgress size={16} /> : null}
              >
                {isDeactivating ? 'Deactivating...' : 'Deactivate & Transfer'}
              </Button>
            </DialogActions>
          </Dialog>
        </Container>
      </Page>
    </>
  );
};

export default Index;

